/**
 * Chooses the data source for this page load (ADR-06 §2.1, D-203).
 *
 *  - Production and the normal dev server: the HTTP client against `/api/office/v1`.
 *  - Fixture mode: **only** when `import.meta.env.DEV` and the URL carries `?fixture=<name>`;
 *    the `ApiClient` is then answered in memory from `src/fixtures/<name>` (frozen clock).
 *    The visual harness (tests/visual, D-203) may add `&theme=dark|light`, `&lang=ar|en`,
 *    `&authed=1` (start with an active session instead of the login screen) and `&chip=0`
 *    (no "fixture data" chip in the header, so the toolbar matches the old app).
 *
 * Nothing here is reachable in a production build: Vite replaces `import.meta.env.DEV` by
 * `false` and the fixture modules are dead code, so no fixture user or PIN ships.
 */
import type { Locale } from "@faylasuf/i18n";
import { createApiClient } from "../api/client.js";
import { createApiSource } from "../api/office.js";
import type { DataSource } from "../api/types.js";
import type { Fixture } from "../fixtures/types.js";
import { createFixtureClient } from "./fixture-client.js";

export interface HarnessOverrides {
  readonly theme?: "dark" | "light";
  readonly lang?: Locale;
  /** `&chip=0`: hide the "fixture data" chip in the header (the visual harness captures without it, D-203). */
  readonly chip?: false;
}

export interface Boot {
  readonly mode: "api" | "fixture";
  readonly source: DataSource;
  /** Clock for the header date line: the fixture's frozen `now`, else the real one. */
  readonly now: () => Date;
  readonly fixture: Fixture | null;
  readonly harness: HarnessOverrides;
}

export async function bootstrap(search: string = window.location.search): Promise<Boot> {
  const params = new URLSearchParams(search);
  const fixtureName = params.get("fixture");

  if (import.meta.env.DEV && fixtureName) {
    const { FIXTURES } = await import("../fixtures/index.js");
    const fixture = FIXTURES[fixtureName];
    if (!fixture) throw new Error(`Unknown fixture "${fixtureName}" (known: ${Object.keys(FIXTURES).join(", ")})`);
    const theme = params.get("theme");
    const lang = params.get("lang");
    const harness: HarnessOverrides = {
      ...(theme === "dark" || theme === "light" ? { theme } : {}),
      ...(lang === "ar" || lang === "en" ? { lang } : {}),
      ...(params.get("chip") === "0" ? { chip: false as const } : {}),
    };
    const client = createFixtureClient(fixture, { authed: params.get("authed") === "1", harness });
    return { mode: "fixture", source: createApiSource(client), now: () => new Date(fixture.now), fixture, harness };
  }

  return { mode: "api", source: createApiSource(createApiClient()), now: () => new Date(), fixture: null, harness: {} };
}
